/**
 * Legacy desktop ERP decommission map (2026-10-08).
 *
 * The desktop staff ERP that used to live in public-web (purchase orders, bills,
 * ledger, invoices, inventory, settings, ...) is retired. The active staff ERP is
 * staff-erp, served under /staff/. Every legacy desktop ERP URL below now renders
 * <LegacyStaffRedirect>, which hard-navigates to the matching staff-erp URL.
 *
 * Rules:
 * - `from` is the old public-web route (react-router pattern).
 * - `to` is the staff-erp path *relative to its basename* (no `/staff` prefix;
 *   getStaffFrontendUrl() adds it). `:param` tokens are filled from `from`.
 * - Record ids are preserved only when the staff-erp module uses the same
 *   Strapi content type keyed by documentId (staff-erp detail routes are
 *   `${basePath}/:documentId`). Otherwise the target is the list page.
 * - Mobile (/m/*) routes are NOT in this table; the mobile app stays as is.
 *
 * Do not add new legacy pages back to routes.js. New staff work goes in staff-erp.
 */

export const LEGACY_ERP_REDIRECTS = [
  // Dashboards
  { key: "dashboard", from: "/dashboard", to: "/" },
  { key: "auth-dashboard", from: "/auth-dashboard", to: "/production-dashboard" },

  // Manufacturing
  { key: "pipeline", from: "/pipeline", to: "/manufacturing/pipeline" },
  { key: "work-order-build", from: "/pipeline/work-orders/:id", to: "/manufacturing/pipeline/:id" },
  { key: "work-orders", from: "/work-orders", to: "/manufacturing/work-orders" },
  { key: "work-order-detail", from: "/work-orders/:id", to: "/manufacturing/work-orders/:id" },
  { key: "build-sheets", from: "/build-sheets", to: "/manufacturing/build-sheets" },
  { key: "build-sheet-viewer", from: "/build-sheets/viewer", to: "/manufacturing/build-sheets" },
  { key: "build-sheet-detail", from: "/build-sheets/:id", to: "/manufacturing/build-sheets/:id" },
  { key: "build-sheet-steps-editor", from: "/build-sheets/:id/steps", to: "/manufacturing/build-sheets/:id" },
  { key: "techdata", from: "/manufacturing/techdata", to: "/manufacturing/techdata" },
  { key: "valve-bodies", from: "/valve-bodies", to: "/manufacturing/valve-bodies" },
  { key: "valve-body-detail", from: "/internal/valve-bodies/:id", to: "/manufacturing/valve-bodies/:id" },
  { key: "parts-bank", from: "/parts-bank/templates", to: "/manufacturing/parts-bank" },
  { key: "parts-bank-template", from: "/parts-bank/templates/:id", to: "/manufacturing/parts-bank/:id" },
  { key: "costs", from: "/costs", to: "/manufacturing/planning-costs" },

  // Inventory
  { key: "inventory", from: "/inventory", to: "/inventory/items" },
  { key: "inventory-detail", from: "/inventory/:id", to: "/inventory/items/:id" },
  { key: "inventory-orders", from: "/inventory-orders", to: "/inventory/orders" },
  { key: "inventory-order-detail", from: "/inventory-orders/:id", to: "/inventory/orders/:id" },

  // Sales / AR
  { key: "sales", from: "/sales", to: "/sales/sales-orders" },
  { key: "sales-order-detail", from: "/sales/:id", to: "/sales/sales-orders/:id" },
  { key: "core-returns", from: "/core-returns", to: "/sales/core-returns" },
  { key: "warranty-claims", from: "/sales/warranty-claims", to: "/sales/warranty-claims" },
  { key: "warranty-claim-detail", from: "/sales/warranty-claims/:documentId", to: "/sales/warranty-claims/:documentId" },
  { key: "warranty-claim-chat", from: "/sales/warranty-claims/:documentId/chat", to: "/sales/warranty-claims/:documentId" },
  { key: "invoices", from: "/invoices", to: "/ar/invoices" },
  { key: "invoice-detail", from: "/invoices/:id", to: "/ar/invoices/:id" },
  // Legacy payments mixed inbound/outbound; staff-erp splits them (/ar/payments vs
  // /ap/payments-out) and the direction is unknown from the URL, so go to the list.
  { key: "payments", from: "/payments", to: "/ar/payments" },
  { key: "payment-detail", from: "/payments/:id", to: "/ar/payments" },
  { key: "customers", from: "/customers", to: "/ar/customers" },
  { key: "customer-detail", from: "/customers/:id", to: "/ar/customers/:id" },

  // Warehouse / purchasing
  { key: "pull-order-detail", from: "/pull-orders/:id", to: "/warehouse/pull-orders/:id" },
  { key: "purchase-orders", from: "/purchase-orders", to: "/warehouse/purchase-orders" },
  { key: "purchase-order-detail", from: "/purchase-orders/:id", to: "/warehouse/purchase-orders/:id" },
  { key: "purchase-receipt-detail", from: "/purchase-receipts/:id", to: "/warehouse/purchase-receipts/:id" },
  { key: "warehouse-shipping", from: "/warehouse/shipping", to: "/warehouse/shipping" },
  { key: "warehouse-receiving", from: "/warehouse/receiving", to: "/warehouse/receiving" },
  { key: "warehouse-receiving-detail", from: "/warehouse/receiving/:id", to: "/warehouse/receiving/:id" },

  // AP
  { key: "operating-expenses", from: "/operating-expenses", to: "/ap/bills" },
  { key: "operating-expense-detail", from: "/operating-expenses/:id", to: "/ap/bills/:id" },
  { key: "pay-bills", from: "/operating-expenses/pay-bills", to: "/ap/pay-bills" },
  { key: "vendors", from: "/vendors", to: "/ap/vendors" },
  { key: "vendor-detail", from: "/vendors/:id", to: "/ap/vendors/:id" },
  { key: "vendor-credits", from: "/vendor-credits", to: "/ap/vendor-credits" },
  { key: "vendor-credit-new", from: "/vendor-credits/new", to: "/ap/vendor-credits/new" },
  { key: "vendor-credit-detail", from: "/vendor-credits/:documentId", to: "/ap/vendor-credits/:documentId" },

  // Accounting
  { key: "ledger", from: "/ledger", to: "/accounting/chart-of-accounts" },
  { key: "account-detail", from: "/ledger/accounts/:documentId", to: "/accounting/chart-of-accounts/:documentId" },
  { key: "posting-rules", from: "/ledger/posting-rules", to: "/accounting/posting-rules" },
  { key: "amortization-schedules", from: "/amortization-schedules", to: "/accounting/recurring" },
  { key: "amortization-schedule-detail", from: "/amortization-schedules/:id", to: "/accounting/recurring/:id" },
  { key: "accounting-home", from: "/accounting/home", to: "/accounting" },
  { key: "accounting-settings", from: "/accounting/settings", to: "/settings/accounting" },

  // HR / planning
  { key: "payroll", from: "/payroll", to: "/hr/payroll" },
  { key: "payroll-run-detail", from: "/payroll/:id", to: "/hr/payroll/:id" },
  { key: "employee-detail", from: "/payroll/employees/:id", to: "/hr/employees/:id" },
  { key: "projections", from: "/projections", to: "/projections" },

  // Admin / media / settings
  { key: "settings", from: "/settings", to: "/settings" },
  { key: "image-gallery", from: "/media/gallery", to: "/pim/image-gallery" },
  // No staff-erp page exists for distributor approvals yet; land on staff home.
  { key: "distributor-approvals", from: "/admin/distributor-approvals", to: "/" },
];

/** Fill `:param` tokens in a staff-erp target path from react-router params. */
export function buildStaffErpPath(to, params = {}) {
  return String(to || "/").replace(/:([A-Za-z0-9_]+)/g, (_, name) => {
    const value = params[name];
    return value ? encodeURIComponent(String(value)) : "";
  }).replace(/\/+$/, "") || "/";
}
